import React from 'react';
import { getMarkDown } from '../utils';
import { useDownloadPDF } from '../hooks/useDownloadPDF';
import '../../../stylesheets/triagebot/RecommendCard.scss';

const RecommendCardLogo = ({ variant }) => (
  <div className={`${variant}-card-logo`}>
    <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60" fill="none">
      <g clipPath={`url(#clip0_${variant})`}>
        <path d="M30.083 15.295C28.3034 15.295 26.5967 14.5881 25.3384 13.3297C24.08 12.0713 23.373 10.3646 23.373 8.585C23.373 6.8054 24.08 5.09868 25.3384 3.84031C26.5967 2.58194 28.3034 1.875 30.083 1.875C31.8626 1.875 33.5694 2.58194 34.8277 3.84031C36.0861 5.09868 36.793 6.8054 36.793 8.585C36.793 10.3646 36.0861 12.0713 34.8277 13.3297C33.5694 14.5881 31.8626 15.295 30.083 15.295ZM30.083 15.295V58.125M38.0755 31.5625C38.838 31.5508 39.592 31.7239 40.2729 32.0672C40.9539 32.4105 41.5415 32.9136 41.9855 33.5336C42.4296 34.1536 42.7168 34.8719 42.8226 35.6271C42.9284 36.3823 42.8496 37.1519 42.593 37.87" stroke="white" strokeWidth="3.75" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M36.2356 11.25H55.9581C62.6506 11.25 52.9581 27.3825 39.0281 22M23.9231 11.25H4.20562C-2.48438 11.25 7.20563 27.3825 21.1356 22M22.0881 31.5625C20.8309 31.5498 19.6199 32.0361 18.7206 32.9148C17.8213 33.7934 17.3071 34.9928 17.2906 36.25C17.2972 37.5308 17.8013 38.759 18.6965 39.6751C19.5916 40.5913 20.8078 41.1237 22.0881 41.16H34.8781C36.1237 41.2002 37.3048 41.7233 38.1717 42.6186C39.0385 43.5139 39.5232 44.7113 39.5232 45.9575C39.5232 47.2037 39.0385 48.4011 38.1717 49.2964C37.3048 50.1917 36.1237 50.7148 34.8781 50.755H26.0856C25.0003 50.7812 23.9698 51.2368 23.2199 52.0218C22.4701 52.8068 22.0621 53.8572 22.0856 54.9425C22.0813 55.5254 22.1969 56.103 22.4253 56.6393C22.6536 57.1756 22.9899 57.6592 23.4131 58.06" stroke="white" strokeWidth="3.75" strokeLinecap="round" strokeLinejoin="round"/>
      </g>
      <defs>
        <clipPath id={`clip0_${variant}`}>
          <rect width="60" height="60" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  </div>
);

/**
 * Generic recommend card for Open Version triage results.
 *
 * @param {string}   variant                   - CSS class prefix: 'selfcare' | 'appointment' | 'emergency' | 'urgentcare'
 * @param {string}   triageTitle               - Card heading
 * @param {string}   triageText                - Card body text
 * @param {object}   message                   - Bot message object (passed to useDownloadPDF)
 * @param {boolean}  useMarkdown               - Render triageText as markdown (selfcare only)
 * @param {string[]} concerningSymptoms        - Optional list of alarming symptoms (emergency only)
 * @param {string}   concerningSymptomsHeader  - Header label for concerning symptoms
 */
const OpenRecommendCard = ({
  variant,
  triageTitle,
  triageText,
  message,
  useMarkdown = false,
  concerningSymptoms = [],
  concerningSymptomsHeader = 'Concerning symptoms:',
}) => {
  const additionalPayload = concerningSymptoms.length > 0 ? { isEmergency: true } : { isEmergency: false};
  const { handleDownload, isDownloading } = useDownloadPDF(message, additionalPayload);

  const recMessage = { type: 'message', text: triageText };

  return (
    <div className={`${variant}-recommend-card`}>
      <div className={`${variant}-card-content`}>
        <h3 className={`${variant}-card-title`}>{triageTitle}</h3>
        <div className={`${variant}-card-description`}>
          {useMarkdown ? getMarkDown(recMessage) : triageText}
        </div>

        {concerningSymptoms.length > 0 && (
          <div className={`${variant}-concerning-symptoms`}>
            <div className={`${variant}-symptom-header`} role='heading' aria-level='3'>
              {concerningSymptomsHeader}
            </div>
            <ul className={`${variant}-symptom-list`}>
              {concerningSymptoms.map((symptom, index) => (
                <li key={`${variant}-symptom-${index}`}>{symptom}</li>
              ))}
            </ul>
          </div>
        )}

        <a
          className={`${variant}-download-link`}
          href='#'
          onClick={e => { e.preventDefault(); handleDownload(); }}
          aria-busy={isDownloading}
        >
          {isDownloading && <span className={`${variant}-download-spinner recommend-download-spinner`} aria-hidden='true' />}
          {isDownloading ? 'Downloading PDF report...' : 'Download your PDF report*'}
        </a>
      </div>
      <RecommendCardLogo variant={variant} />
    </div>
  );
};

export default OpenRecommendCard;
